
import { useState } from 'react';
import { 
  createLicenseKey,
  updateLicenseKey
} from '@/utils/licenseService';
import { LicenseKey, ResellerPlan } from '@/types/licenseKey';
import { useToast } from '@/hooks/use-toast';

export const useLicenseKeys = (
  licenseKeys: LicenseKey[],
  setLicenseKeys: React.Dispatch<React.SetStateAction<LicenseKey[]>>
) => {
  const { toast } = useToast();
  const [isCreatingLicense, setIsCreatingLicense] = useState(false);
  
  // Handle license creation
  const handleCreateLicense = async (data: {
    domain?: string;
    plan: ResellerPlan;
    expiresAt: Date;
    notes?: string;
  }) => {
    setIsCreatingLicense(true);
    try {
      const newKey = await createLicenseKey(data);
      setLicenseKeys(prev => [newKey, ...prev]);
      
      toast({
        title: "License Created",
        description: `Successfully created license key: ${newKey.key.substring(0, 10)}...`,
      });
      
      return newKey;
    } catch (error) {
      console.error('Error creating license:', error);
      toast({
        title: "Error",
        description: "Failed to create license key",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsCreatingLicense(false);
    }
  };
  
  // Handle license suspension
  const handleSuspendLicense = async (licenseId: string) => {
    try {
      const updatedLicense = await updateLicenseKey(licenseId, { status: 'suspended' });
      
      setLicenseKeys(prev => prev.map(key => 
        key.id === licenseId ? updatedLicense : key
      ));
      
      toast({
        title: "License Suspended",
        description: `License has been suspended`,
      });
      
      return updatedLicense;
    } catch (error) {
      console.error('Error suspending license:', error);
      toast({
        title: "Error",
        description: "Failed to suspend license",
        variant: "destructive",
      });
      throw error;
    }
  };
  
  // Handle license reactivation
  const handleReactivateLicense = async (licenseId: string) => {
    try {
      const updatedLicense = await updateLicenseKey(licenseId, { status: 'active' });
      
      setLicenseKeys(prev => prev.map(key => 
        key.id === licenseId ? updatedLicense : key
      ));
      
      toast({
        title: "License Reactivated",
        description: `License has been reactivated`,
      });
      
      return updatedLicense;
    } catch (error) {
      console.error('Error reactivating license:', error);
      toast({
        title: "Error",
        description: "Failed to reactivate license",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Handle license deletion
  const handleDeleteLicense = async (licenseId: string) => {
    try {
      // Here, in a real application, you would call an API to delete the license
      // For now, we'll just remove it from the local state
      setLicenseKeys(prev => prev.filter(key => key.id !== licenseId));
      
      toast({
        title: "License Deleted",
        description: `License has been deleted`,
      });
    } catch (error) {
      console.error('Error deleting license:', error);
      toast({
        title: "Error",
        description: "Failed to delete license",
        variant: "destructive",
      });
      throw error;
    }
  };
  
  return {
    isCreatingLicense,
    handleCreateLicense,
    handleSuspendLicense,
    handleReactivateLicense,
    handleDeleteLicense
  };
};
